import React from 'react';
import { NavLink } from 'react-router-dom';
import { ExitIcon } from '@radix-ui/react-icons';
import { Avatar } from '../Avatar';
import './Sidebar.scss';

export interface SidebarOption {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path: string;
  group?: 'primary' | 'secondary';
  badge?: number | string;
}

export interface SidebarProps {
  options: SidebarOption[];
  onLogout?: () => void;
  onNavigate?: () => void;
  user?: {
    name?: string;
    email?: string;
    avatar?: string | null;
    role?: string;
  } | null;
}

export const Sidebar: React.FC<SidebarProps> = ({ options, onLogout, onNavigate, user }) => {
  const displayName = user?.name?.trim() || user?.email || 'Admin User';
  const displayRole = user?.role || 'System Admin';

  const primaryOptions = options.filter((opt) => opt.group !== 'secondary');
  const secondaryOptions = options.filter((opt) => opt.group === 'secondary');

  const renderOption = (opt: SidebarOption) => (
    <NavLink
      key={opt.key}
      to={opt.path}
      className={({ isActive }) =>
        [
          'sidebar-menu-item',
          isActive && 'selected',
          opt.group === 'secondary' && 'sidebar-menu-item--secondary',
        ]
          .filter(Boolean)
          .join(' ')
      }
      onClick={onNavigate}
    >
      {opt.icon && <span className="sidebar-menu-icon">{opt.icon}</span>}
      <span className="sidebar-menu-label">{opt.label}</span>
      {opt.badge != null && (
        <span className="sidebar-menu-badge">{opt.badge}</span>
      )}
    </NavLink>
  );

  return (
    <aside className="sidebar-root">
      <div className="sidebar-logo">
        <img src="/assets/images/logo.svg" alt="Saathi4U" />
      </div>

      <nav className="sidebar-menu">
        {primaryOptions.map(renderOption)}

        {secondaryOptions.length > 0 && (
          <>
            <div className="sidebar-menu-divider" aria-hidden="true" />
            {secondaryOptions.map(renderOption)}
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            className="sidebar-profile-card"
            aria-label="Logout"
          >
            <Avatar
              name={displayName}
              src={user?.avatar}
              size="small"
              className="sidebar-profile-avatar"
            />
            <span className="sidebar-profile-copy">
              <span className="sidebar-profile-name">{displayName}</span>
              <span className="sidebar-profile-role">{displayRole}</span>
            </span>
            <ExitIcon />
          </button>
        )}
      </div>
    </aside>
  );
};
